/* eslint-disable no-restricted-syntax */
import { RouteComponentProps } from "@reach/router";
import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect
} from "react";
import { Dayjs } from "dayjs";
import JSZip from "jszip";
import style from "./reports.module.scss";
import Button from "../components/button/Button";
import RangePicker from "../components/range-picker/RangePicker";
import Table, { Column } from "../components/table/Table";
import { resources } from "../utils/resources";
import { EcommerceCards } from "../utils/helpers/types";
import styles from "./self-service.module.scss";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import Modal from "../components/modal/Modal";
import {
  downloadCSV,
  getEcommerceCardDetails
} from "../utils/helpers/data/ecommerce-report";
import Select, { ValueType } from "../components/select/Select";
import ToolTip from "../components/ToolTip/ToolTip";

const EcommerceReportsPage: FunctionComponent<RouteComponentProps> = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null
  ]);

  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [card, setCard] = useState<EcommerceCards[]>([]);
  const [loading, setLoading] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [reset, setReset] = useState(false);
  const [cardsOnView, setCardsOnView] = useState<EcommerceCards | null>(null);

  const [initialFetch, setInitialFetch] = useState(true);

  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filtersReset, setFiltersReset] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedFileNumber, setSelectedFileNumber] = useState<string>("");

  let emptyTextValue = "FILTER RECORDS";

  if (filtersApplied && card.length === 0) {
    emptyTextValue = "No data";
  }

  if (filtersReset) {
    emptyTextValue = "FILTER RECORDS";
  }

  const resetFilters = () => {
    setFiltersReset(true);
    setReset(true);
    setDateRange([null, null]);
    setPagination({ ...initialPaginationData });
    setCard([]);
  };

  const showResetFilter = dateRange[0] || dateRange[1];

  const formatDateForApi = (date: Dayjs | null) => {
    return date ? date.format("YYYY-MM-DD") : "";
  };

  const fetchCards = async (
    pageNumber: number = pagination.pageNumber,
    pageSize: number = pagination.pageSize
  ) => {
    setLoading(true);
    setReset(false);

    let formattedStartTime = "";
    let formattedEndTime = "";

    if (dateRange[0] && dateRange[1]) {
      formattedStartTime = formatDateForApi(dateRange[0]).concat(" 00:00:00");
      formattedEndTime = formatDateForApi(dateRange[1]).concat(" 23:59:00");
    }

    try {
      const { data, error, message } = await getEcommerceCardDetails(
        pageNumber,
        pageSize,
        formattedStartTime,
        formattedEndTime
      );

      if (error || !data) {
        notify("error", `Unable to fetch cards: ${message}`);
        return;
      }

      setCard(data.records);
      setPagination({
        ...pagination,
        pageNumber,
        total: data.total
      });
      setTotalRecords(data.total);
    } catch (error) {
      notify("error", `An error occurred while fetching cards: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const generateFileNumbers = () => {
    const fileNumber = [];
    const totalFileNumbers = Math.ceil(totalRecords / 500);

    if (totalRecords > 500) {
      fileNumber.push("All");
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= totalFileNumbers; i++) {
      fileNumber.push(`${i}`);
    }

    return fileNumber;
  };

  const fileNumber = generateFileNumbers();

  const columns: Column<EcommerceCards>[] = [
    {
      title: "Retrival ref",
      dataIndex: "retrievalReferenceNr",
      key: "retrievalReferenceNr"
    },
    {
      title: "Response code",
      dataIndex: "rspCodeRsp",
      key: "rspCodeRsp"
    },
    {
      title: "Card Product",
      dataIndex: "cardProduct",
      key: "cardProduct"
    },
    {
      title: "Card acceptor name",
      dataIndex: "cardAcceptorNameLoc",
      key: "cardAcceptorNameLoc"
    },
    {
      title: "Trans amt res",
      dataIndex: "tranAmountRsp",
      key: "tranAmountRsp"
    },
    {
      title: "Trans amt req",
      dataIndex: "tranAmountReq",
      key: "tranAmountReq"
    },
    {
      title: "",
      render: (_: unknown, cards: EcommerceCards) => {
        const options: ContextOption[] = [
          {
            label: "View",
            onClick: () => {
              setCardsOnView(cards);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });

      if (dateRange[0] && dateRange[1]) {
        await fetchCards();
        setFiltersApplied(true);
        setFiltersReset(false);
      } else {
        notify("error", "Kindly select both start date and end date.");
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notify("error", "An error occurred while fetching cards.", error);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setLoading(true);

      let startDate = "";
      let endDate = "";

      if (dateRange[0] && dateRange[1]) {
        startDate = formatDateForApi(dateRange[0]).concat(" 00:00:00");
        endDate = formatDateForApi(dateRange[1]).concat(" 23:59:00");

        if (selectedFileNumber === "All") {
          const zip = new JSZip();

          for (const file of fileNumber) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const response = await downloadCSV(startDate, endDate, file);

              if (response.data) {
                zip.file(
                  `Ecommerce_transactions_csv_${file}.csv`,
                  response.data
                );
              } else {
                console.error(`No data for fileNumber: ${file}`);
              }
            } catch (error) {
              console.error(
                `Error downloading CSV for fileNumber: ${file}`,
                error
              );
            }
          }

          const zipBlob = await zip.generateAsync({ type: "blob" });

          const url = window.URL.createObjectURL(zipBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "Ecommerce_transactions_csv_All.zip";

          a.click();

          window.URL.revokeObjectURL(url);
        } else if (selectedFileNumber) {
          const response = await downloadCSV(
            startDate,
            endDate,
            selectedFileNumber
          );

          if (response.data) {
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Ecommerce_transactions_csv_${selectedFileNumber}.csv`;

            a.click();

            window.URL.revokeObjectURL(url);
          } else {
            notify("error", `No data for fileNumber: ${selectedFileNumber}`);
          }
        } else {
          notify("error", "Please select a file number.");
        }
      } else {
        notify("error", "Please select both start date and end date.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialFetch) {
      fetchCards();
    } else {
      setInitialFetch(false);
    }
  }, [pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">
        E-commerce Card Report
      </h1>
      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className="input-group">
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="D MMM YYYY"
                placeholder="Select date range"
              />
            </div>
            <span className={style.button} />
            <div className="input-group half-width">
              <Select
                value={selectedFileNumber}
                onSelect={(value: ValueType) =>
                  setSelectedFileNumber(value as string)
                }
                options={fileNumber.map(file => ({
                  value: file,
                  label: file
                }))}
                placeholder="File No"
                responsive
                id="fileNumberDropdown"
              />
            </div>
            <span className="question">
              <span className="required" />
              <ToolTip
                toolTip="Each file no conatins 500 records of the filtered data starting from the most recent date"
                anchor={
                  <img
                    alt="info tooltip"
                    src={resources.icons.INFO_CIRCLE_SOLID}
                    className="generic-icon small"
                  />
                }
              />
            </span>
          </div>

          <div className={style.inputView}>
            <Button
              type="accent"
              size="small"
              className="strong no-pdf-print"
              onClick={handleViewReport}
            >
              Filter record
            </Button>
            <span className={style.button} />

            <Button
              type="accent"
              size="small"
              className="strong no-pdf-print"
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
            <span className={style.button} />
          </div>
        </div>

        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <Table
        dataSource={card || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchCards}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        emptyText={emptyTextValue}
        className="smaller"
        perPageOptions={[
          { label: "5", value: 5 },
          { label: "10", value: 10 }
        ]}
      />

      <CardViewModal
        card={cardsOnView}
        cancel={() => setCardsOnView(null)}
        refresh={fetchCards}
        visible={Boolean(cardsOnView)}
      />
    </main>
  );
};

interface CardEditModalProps {
  card: EcommerceCards | null;
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
}

const CardViewModal: FunctionComponent<CardEditModalProps> = ({
  card: _card,
  cancel,
  visible,
  refresh
}) => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<EcommerceCards | null>(_card);

  useEffect(() => {
    setTasks(_card);
  }, [_card]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Acquired institution code:</span>
            <span className={styles.value}>
              {tasks?.acquiringInstIdCode || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Authorization ID response:</span>
            <span className={styles.value}>{tasks?.authIdRsp || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Card acceptor Id Code:</span>
            <span className={styles.value}>
              {tasks?.cardAcceptorIdCode || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Card acceptor name Loc:</span>
            <span className={styles.value}>
              {tasks?.cardAcceptorNameLoc || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Card product:</span>
            <span className={styles.value}>{tasks?.cardProduct || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Date time request:</span>
            <span className={styles.value}>{tasks?.datetimeReq || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Date time tranLocal</span>
            <span className={styles.value}>
              {tasks?.datetimeTranLocal || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Extended TranType:</span>
            <span className={styles.value}>
              {tasks?.extendedTranType || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>From AccountId:</span>
            <span className={styles.value}>
              {tasks?.fromAccountId || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Merchant Type:</span>
            <span className={styles.value}>{tasks?.merchantType || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Message Type:</span>
            <span className={styles.value}>{tasks?.messageType || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Pan:</span>
            <span className={styles.value}>{tasks?.pan || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Pan Encrypted :</span>
            <span className={styles.value}>{tasks?.panEncrypted || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Payee:</span>
            <span className={styles.value}>{tasks?.payee || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Payer AccountId:</span>
            <span className={styles.value}>
              {tasks?.payerAccountId || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Pos Entry Mode:</span>
            <span className={styles.value}>{tasks?.posEntryMode || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Pos Terminal Type:</span>
            <span className={styles.value}>
              {tasks?.posTerminalType || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Post TranCustId :</span>
            <span className={styles.value}>
              {tasks?.postTranCustId || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Post TranId:</span>
            <span className={styles.value}>{tasks?.postTranId || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Retrieval ReferenceNr:</span>
            <span className={styles.value}>
              {tasks?.retrievalReferenceNr || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>rspCodeReq:</span>
            <span className={styles.value}>{tasks?.rspCodeReq || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>rspCodeRsp :</span>
            <span className={styles.value}>{tasks?.rspCodeRsp || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settle AmountReq :</span>
            <span className={styles.value}>
              {tasks?.settleAmountReq || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settle AmountRsp:</span>
            <span className={styles.value}>
              {tasks?.settleAmountRsp || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settle Currency Code:</span>
            <span className={styles.value}>
              {tasks?.settleCurrencyCode || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settle TranFeeReq:</span>
            <span className={styles.value}>
              {tasks?.settleTranFeeReq || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settle TranFeeRsp:</span>
            <span className={styles.value}>
              {tasks?.settleTranFeeRsp || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Sink NodeName :</span>
            <span className={styles.value}>{tasks?.sinkNodeName || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Source NodeName:</span>
            <span className={styles.value}>
              {tasks?.sourceNodeName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>System TraceAuditNr:</span>
            <span className={styles.value}>
              {tasks?.systemTraceAuditNr || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Terminal Id:</span>
            <span className={styles.value}>{tasks?.terminalId || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Totals Group:</span>
            <span className={styles.value}>{tasks?.totalsGroup || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>tranAmountReq:</span>
            <span className={styles.value}>
              {tasks?.tranAmountReq || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>tranCurrencyCode:</span>
            <span className={styles.value}>
              {tasks?.tranCurrencyCode || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>tranReversed :</span>
            <span className={styles.value}>{tasks?.tranReversed || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>tranType:</span>
            <span className={styles.value}>{tasks?.tranType || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default EcommerceReportsPage;
